// PasosBienvenida.js
//
// Los dos primeros pasos del wizard de verificación (la bienvenida y las
// preguntas de "quién eres") y las piezas visuales de la piel del onboarding
// (#1525) que también usa el resto del wizard: encabezado, tarjeta, opciones,
// botones.
//
// Hasta el 09-sep-2026 vivían en una pantalla aparte (/bienvenida,
// views/onboarding/Bienvenida.js). Sebastián eligió la variante C del mockup
// `tools/mockups/smartdx_mockups_bienvenida_registro.html`: la bienvenida y las
// preguntas se integran al wizard que ya existe, como primeros pasos — un solo
// flujo, un solo indicador de progreso, una sola ruta.
//
// El orden importa y es el mismo de antes:
//   0. Bienvenida — UNA idea en pantalla: ya tienes cuenta, tienes 14 días, y
//      estos son los papeles que vas a necesitar. El aviso de los documentos va
//      ANTES de pedirlos: el que abandona el registro es el que se topa con
//      "sube tu certificado" sin haberlo buscado (item #878).
//   1. Quién eres — para saber a quién tenemos adentro. OBLIGATORIAS desde #1599
//      (Sebastián, 10-oct-2026: "nombre completo si o si", "nos interesa mas info
//      para calificar"): no hay "Saltar por ahora" y Continuar se habilita solo con
//      todo lo obligatorio respondido. Reemplaza el "OPCIONALES" de #1525. Las
//      opcionales son las que Sebastián pidió después ("deja estos ultimos como opcionales
//      no obligatorios"): herramientas y frustraciones, y desde #1738 la universidad del
//      médico general y del especialista (R14), marcadas con "opcional" junto a su pregunta
//      (R54). «Cómo nos conociste» dejó de serlo: desde #1738 R18 es obligatoria.
//      Son CUATRO pantallas dentro del mismo paso (mockup B de
//      `tools/mockups/smartdx_mockups_onboarding_calificar.html`): 1 de 4 tú, 2 de 4
//      tu semana, 3 de 4 "Tu turno" (si hace turnos) o "Qué haces" (si no), 4 de 4
//      cómo nos conociste. Siguen siendo 4 rayitas: las pantallas las lleva el wizard.
//
// Piel de #1525 (mockup A, `smartdx_mockups_onboarding_higgsfield.html`): los
// colores salen de tokens de theme.js (`--bg-elevated`, `--text-primary`,
// `--accent-onboarding`...). Los botones y las opciones son <button> propios y
// NO `Chip`/`Button` de MUI: heredan overrides globales de theme.js que no se
// ven leyendo el JSX (alto de 40 px, colores de menú) y R17 pide 44 px reales.
//
// Todo es presentacional a propósito: el estado (en qué paso va, qué respondió,
// qué archivos subió) lo lleva VerificationWizard, que es el que habla con el
// backend.

import React from "react";
import { Box, Typography, keyframes, useMediaQuery } from "@mui/material";
import {
  VISTAS,
  alternarEnLista,
  alternarEquipo,
  alternarHerramienta,
  camposDePantalla,
  campoRespondido,
  conteoDePantalla,
  esOpcional,
  esOpcionalEn,
  maxProximoTurno,
  pantallasQuienEres,
} from "../../helpers/onboarding-completo";
import { ETAPAS_ALTA, PROFESIONES_NO_MEDICO } from "../../helpers/etapas";
import { ESPECIALIDADES, filtrarOpciones, sinTildes } from "../../helpers/especialidades";
import { UNIVERSIDADES } from "../../helpers/universidades";
import { CANALES_OPCIONES } from "../../helpers/canales";
import { tituloUniversidad } from "../../helpers/etapas";
import { reaccionCarga, reaccionEquipo, reaccionTurnos, textosDeEtapa } from "../../helpers/onboarding-etapa-textos";
import { comoSeCalculo, diasHasta, equivalencias, horasAlAno, horasPorTurno, lugaresDelTurno } from "../../helpers/onboarding-horas";

// Íconos (lucide, los mismos del mockup).
const Svg = ({ children, size = 18, ...rest }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ flex: "none" }}
    {...rest}
  >
    {children}
  </svg>
);
export const Ic = {
  Back: (p) => (
    <Svg {...p}>
      <path d="m15 18-6-6 6-6" />
    </Svg>
  ),
  Id: (p) => (
    <Svg {...p}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="9" cy="11" r="2" />
      <path d="M15 9h3M15 13h3M6 16c.5-1.5 1.7-2 3-2s2.5.5 3 2" />
    </Svg>
  ),
  File: (p) => (
    <Svg {...p}>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5M9 13h6M9 17h4" />
    </Svg>
  ),
  Up: (p) => (
    <Svg {...p}>
      <path d="M12 16V4M7 9l5-5 5 5M5 20h14" />
    </Svg>
  ),
  Ok: (p) => (
    <Svg {...p}>
      <path d="M20 6 9 17l-5-5" />
    </Svg>
  ),
  Out: (p) => (
    <Svg {...p}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
    </Svg>
  ),
};

// #1738 R1: las 6 etapas del alta salen de `helpers/etapas.js` (fuente única de etiquetas).
export const ETAPAS = ETAPAS_ALTA;

export const AMBITOS = [
  { value: "publico", label: "Hospital o servicio público" },
  { value: "privado", label: "Consulta o clínica privada" },
];

export const CARGAS = [
  { value: "<1h", label: "Menos de 1h" },
  { value: "1-2h", label: "1 a 2h" },
  { value: "2-4h", label: "2 a 4h" },
  { value: "+4h", label: "Más de 4h" },
];

// #1525 R12 (decisión P2 de Sebastián, 07-oct-2026): "No · 1 · 2-3 · 4+". Los
// `value` son EXACTAMENTE los que acepta el backend (`TurnosSemana` en
// user_api/models.py); cambiar uno acá sin cambiarlo allá da 400 en el PATCH.
export const TURNOS = [
  { value: "0", label: "No hago turnos" },
  { value: "1", label: "1" },
  { value: "2-3", label: "2 a 3" },
  { value: "4+", label: "4 o más" },
];

export const HOSPITALIZADOS = [
  { value: true, label: "Sí" },
  { value: false, label: "No" },
];

// #1599 R16: tipo de turno, agrupado como `tiposGrup` del mockup B (los 4 con
// hospitalizados primero, Urgencia aparte). Los `value` son los del backend
// (`user_api/onboarding_completo.py`).
export const TIPOS_TURNO_GRUPOS = [
  {
    titulo: "Con pacientes hospitalizados",
    opciones: [
      { value: "uci", label: "UCI", sub: "Cuidados intensivos" },
      { value: "uti", label: "UTI", sub: "Cuidados intermedios" },
      { value: "sala", label: "Sala", sub: "Pacientes en sala" },
      { value: "urgencia_hosp", label: "Urgencia hospitalizados", sub: "Pacientes que esperan cama" },
    ],
  },
  {
    titulo: "Atención de urgencia",
    opciones: [{ value: "urgencia", label: "Urgencia", sub: "Atención de urgencia" }],
  },
];

export const DIAS_SEMANA = [
  { value: "lun", label: "Lun" },
  { value: "mar", label: "Mar" },
  { value: "mie", label: "Mié" },
  { value: "jue", label: "Jue" },
  { value: "vie", label: "Vie" },
  { value: "sab", label: "Sáb" },
  { value: "dom", label: "Dom" },
];

// Las opciones de herramientas (opcionales, labels del mockup B).
export const HERRAMIENTAS_OPCIONES = [
  { value: "claude", label: "Claude" },
  { value: "chatgpt", label: "ChatGPT" },
  { value: "google_drive", label: "Google Drive" },
  { value: "bloc_notas", label: "Bloc de notas" },
  { value: "word", label: "Word" },
  { value: "papel", label: "Papel a mano" },
  { value: "notas_celular", label: "Notas del celular" },
  { value: "ninguna", label: "Ninguna" },
  { value: "otra", label: "Otra" },
];

// #1850: con quién trabaja en el turno.
export const EQUIPO_OPCIONES = [
  { value: "internos", label: "Internos" },
  { value: "becados", label: "Becados" },
  { value: "backup", label: "Backup" },
  { value: "nadie", label: "Nadie" },
];

export const FRUSTRACIONES_OPCIONES = [
  { value: "evoluciones", label: "Escribir evoluciones" },
  { value: "ingresos", label: "Ingresos" },
  { value: "epicrisis", label: "Epicrisis" },
  { value: "entrega_turno", label: "Entrega de turno" },
  { value: "examenes", label: "Ordenar exámenes" },
  { value: "indicaciones", label: "Indicaciones" }, // #1681 D62: sin nombrar recetas; el valor interno no cambia
  { value: "otro", label: "Otro" },
];

// #1738 R17: la tabla de canales vive en `helpers/canales.js` (alta, faltante y ficha 360 leen la misma).
export { CANALES_OPCIONES };

// ── Piezas de la piel (mockup A) ────────────────────────────────────────────
const EASE = "cubic-bezier(0.22,1,0.36,1)";
// #1850: movimiento de Quién eres. Todo se apaga con prefers-reduced-motion (`sinMovimiento`).
const saltoOpcion = keyframes`0% { transform: scale(1); } 40% { transform: scale(1.07); } 100% { transform: scale(1); }`;
const entrarPantalla = keyframes`from { opacity: 0; transform: translateX(28px); } to { opacity: 1; transform: none; }`;
const brilloCifra = keyframes`0% { text-shadow: 0 0 0 rgba(var(--color-primary-rgb), 0); } 30% { text-shadow: 0 0 18px rgba(var(--color-primary-rgb), .9); } 100% { text-shadow: 0 0 0 rgba(var(--color-primary-rgb), 0); }`;
const sinMovimiento = { "@media (prefers-reduced-motion: reduce)": { animation: "none" } };
// Un toque corto al elegir, solo donde el navegador lo permite (Android; iOS lo ignora).
const vibrarCorto = () => {
  try {
    if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(8);
  } catch (_) {
    /* sin vibración */
  }
};

/** Etiqueta mono en mayúsculas ("Quién eres · 1 de 2"). */
export const Etiqueta = ({ children, ...rest }) => (
  <Box
    {...rest}
    sx={{
      font: '600 11px "SF Mono", ui-monospace, Menlo, Consolas, monospace',
      letterSpacing: ".12em",
      color: "var(--text-faint)",
      textTransform: "uppercase",
      mb: "8px",
    }}
  >
    {children}
  </Box>
);

/**
 * Encabezado grande de cada paso: chip opcional, título en dos tonos y bajada. `compactoMovil`
 * (#1850, Quién eres): en el celular el título va en una línea de 26 px y sin bajada, para que
 * cada pantalla de preguntas quepa en una vista de 390×844 sin scroll.
 */
export const Encabezado = ({ chip, titulo, resto, sub, pequeno = false, compactoMovil = false }) => (
  <Box>
    {chip && (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          backgroundColor: "var(--text-primary)",
          color: "var(--bg-base)",
          borderRadius: 999,
          p: "6px 13px",
          fontSize: 13,
          fontWeight: 500,
          mb: "18px",
        }}
      >
        <Ic.Ok size={15} /> {chip}
      </Box>
    )}
    <Typography
      component="h2"
      sx={{
        fontSize: { xs: compactoMovil ? 26 : 32, md: compactoMovil ? 34 : pequeno ? 40 : 52 },
        lineHeight: 1.06,
        letterSpacing: "-0.035em",
        fontWeight: 500,
        m: 0,
        textWrap: "balance",
      }}
    >
      {titulo}{" "}
      <Box component="span" sx={{ display: compactoMovil ? "inline" : "block", color: "var(--text-faint)" }}>
        {resto}
      </Box>
    </Typography>
    {sub && (
      <Typography
        component="p"
        sx={{
          display: compactoMovil ? "none" : "block",
          color: "var(--text-muted)",
          fontSize: { xs: 15.5, md: 17 },
          lineHeight: 1.55,
          mt: { xs: "10px", md: "14px" },
          mb: 0,
          "& strong, & b": { color: "var(--text-primary)", fontWeight: 600 },
        }}
      >
        {sub}
      </Typography>
    )}
  </Box>
);

/** Tarjeta (`fcard`). */
export const Tarjeta = ({ children, ...rest }) => (
  <Box
    {...rest}
    sx={{
      backgroundColor: "var(--bg-elevated)",
      border: "1px solid rgba(var(--fg-rgb),0.07)",
      borderRadius: "18px",
      p: "16px 18px",
    }}
  >
    {children}
  </Box>
);

/** Etiqueta de un campo (`flabel`) con nota chica opcional a la derecha. */
export const EtiquetaCampo = ({ children, nota, id, ...rest }) => (
  <Box
    component="label"
    htmlFor={id}
    {...rest}
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "baseline",
      gap: "8px",
      fontSize: 13,
      color: "var(--text-muted)",
      mb: "8px",
    }}
  >
    <span>{children}</span>
    {nota && <Box component="small" sx={{ color: "var(--text-faint)", fontSize: 12 }}>{nota}</Box>}
  </Box>
);

/** Campo de texto con la forma de `.inp` del mockup (un <input> real). */
export const campoSx = {
  width: "100%",
  boxSizing: "border-box",
  minHeight: 48,
  fontFamily: "inherit",
  fontSize: 16,
  color: "var(--text-primary)",
  backgroundColor: "var(--bg-input)",
  border: "1px solid rgba(var(--fg-rgb),0.08)",
  borderRadius: "12px",
  p: "12px 14px",
  outline: "none",
  transition: `border-color 160ms ${EASE}, box-shadow 160ms ${EASE}`,
  "&::placeholder": { color: "var(--text-faint)" },
  "&:focus": {
    borderColor: "var(--accent-onboarding)",
    boxShadow: "0 0 0 3px rgba(var(--color-primary-rgb),0.18)",
  },
  "&:-webkit-autofill": {
    WebkitBoxShadow: "0 0 0 1000px var(--bg-input) inset",
    WebkitTextFillColor: "var(--text-primary)",
    caretColor: "var(--text-primary)",
  },
};

/**
 * Una opción tocable. `grande` es la `optb` del mockup (turnos, hospitalizados); sin eso es la
 * `op` de las otras. `forma` suma dos variantes de #1599: "tarjeta" (tipo de turno, con una
 * frase debajo, `.tp .op` del mockup B) y "dia" (lunes a domingo, `.days .op`). Siempre ≥ 44 px
 * de alto (R17, R31). Es un <button> con `role` radio o checkbox, no un `Chip`.
 */
export const Opcion = ({
  activo,
  onClick,
  children,
  grande = false,
  forma = "normal",
  sub = "",
  role = "radio",
  ...rest
}) => {
  const tarjeta = forma === "tarjeta";
  const dia = forma === "dia";
  return (
    <Box
      component="button"
      type="button"
      role={role}
      aria-checked={Boolean(activo)}
      onClick={(e) => {
        if (!activo) vibrarCorto();
        onClick?.(e);
      }}
      {...rest}
      sx={{
        animation: activo ? `${saltoOpcion} 280ms ${EASE}` : "none",
        ...sinMovimiento,
        display: "inline-flex",
        flexDirection: tarjeta ? "column" : "row",
        alignItems: tarjeta ? "flex-start" : "center",
        justifyContent: grande || dia ? "center" : tarjeta ? "center" : "flex-start",
        gap: tarjeta ? "2px" : "6px",
        textAlign: grande ? "center" : "left",
        cursor: "pointer",
        fontFamily: "inherit",
        borderRadius: tarjeta ? "16px" : 999,
        border: "1px solid",
        // #1850: en el celular, más bajas (nunca bajo 44 px, R17/R31) para que cada pantalla de
        // Quién eres quepa en una vista sin scroll.
        minHeight: grande ? { xs: 46, md: 50 } : tarjeta ? { xs: 50, md: 56 } : 44,
        minWidth: dia ? 44 : 0,
        p: grande
          ? { xs: "8px 6px", md: "10px 8px" }
          : tarjeta
            ? { xs: "8px 14px", md: "9px 16px" }
            : dia
              ? "10px 0"
              : { xs: "8px 14px", md: "10px 16px" },
        fontSize: grande ? { xs: 14.5, md: 15.5 } : dia ? 14 : { xs: 14, md: 14.5 },
        lineHeight: 1.2,
        fontWeight: activo ? 500 : 400,
        color: activo ? "var(--bg-base)" : grande ? "var(--text-primary)" : "var(--text-muted)",
        backgroundColor: activo ? "var(--text-primary)" : grande ? "var(--bg-elevated)" : "var(--bg-input)",
        borderColor: activo
          ? "var(--text-primary)"
          : grande
            ? "rgba(var(--fg-rgb),0.14)"
            : "rgba(var(--fg-rgb),0.08)",
        transition: `all 160ms ${EASE}`,
        "&:focus-visible": { outline: "2px solid var(--accent-onboarding)", outlineOffset: 2 },
        "&:hover": { borderColor: "rgba(var(--fg-rgb),0.3)" },
      }}
    >
      {tarjeta ? (
        <>
          <span>{children}</span>
          {sub && (
            <Box component="small" sx={{ fontSize: 12.5, opacity: 0.7, fontWeight: 400 }}>
              {sub}
            </Box>
          )}
        </>
      ) : (
        children
      )}
    </Box>
  );
};

/** Botón principal (`cta`). */
export const Cta = ({ children, ...rest }) => (
  <Box
    component="button"
    type="button"
    {...rest}
    sx={{
      display: "inline-flex",
      justifyContent: "center",
      alignItems: "center",
      gap: "8px",
      fontFamily: "inherit",
      border: 0,
      cursor: "pointer",
      borderRadius: 999,
      p: "15px 30px",
      fontSize: 16,
      fontWeight: 600,
      backgroundColor: "var(--text-primary)",
      color: "var(--bg-base)",
      minHeight: 52,
      transition: `opacity 160ms ${EASE}, transform 160ms ${EASE}`,
      "&:hover:not(:disabled)": { transform: "translateY(-1px)" },
      "&:disabled": { opacity: 0.45, cursor: "not-allowed" },
      "&:focus-visible": { outline: "2px solid var(--accent-onboarding)", outlineOffset: 3 },
    }}
  >
    {children}
  </Box>
);

/** Botón secundario sin borde (`ghost`). */
export const Fantasma = ({ children, ...rest }) => (
  <Box
    component="button"
    type="button"
    {...rest}
    sx={{
      display: "inline-flex",
      justifyContent: "center",
      alignItems: "center",
      fontFamily: "inherit",
      border: 0,
      background: "none",
      cursor: "pointer",
      borderRadius: 999,
      p: "15px 22px",
      fontSize: 15,
      fontWeight: 500,
      color: "var(--text-muted)",
      minHeight: 52,
      "&:hover:not(:disabled)": { color: "var(--text-primary)" },
      "&:disabled": { opacity: 0.45, cursor: "not-allowed" },
      "&:focus-visible": { outline: "2px solid var(--accent-onboarding)", outlineOffset: 3 },
    }}
  >
    {children}
  </Box>
);

/** Fila de botones: en una fila en escritorio, apilados a todo el ancho en el celular. */
export const FilaCta = ({ children }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: { xs: "column", md: "row" },
      alignItems: { xs: "stretch", md: "center" },
      gap: "8px",
    }}
  >
    {children}
  </Box>
);

/** Paso 0 — el saludo y los días de prueba reales. #1850: ya no anuncia papeles (se piden al suscribirse). */
export const PasoBienvenida = ({
  nombre,
  tipoPapeles,
  diasPrueba,
  reintro = false,
  hastaFecha = "",
  conDocumentos = false,
}) => {
  // Cubre R9 — .claude/specs/rescate-inactivos/. Cuenta del rescate: mismo
  // diseño, otro texto ("Hola de nuevo"). Sin la caja de papeles si ya los
  // entregó. `hastaFecha` sale de trial_ends_at ("31 de octubre"); sin fecha se
  // omite, nunca se inventa.
  if (reintro) {
    return (
      <>
        <Encabezado
          chip="Tu prueba sigue abierta"
          titulo={nombre ? `Hola de nuevo, ${nombre},` : "Hola de nuevo,"}
          resto="te extendimos la prueba"
          sub={
            <>
              {diasPrueba ? (
                <>
                  Tienes <strong>{diasPrueba} día{diasPrueba === 1 ? "" : "s"}</strong> para usar
                  SmartDx completo{hastaFecha ? `, hasta el ${hastaFecha}` : ""}.{" "}
                </>
              ) : (
                ""
              )}
              Te mostramos lo nuevo en un minuto.
            </>
          }
        />
      </>
    );
  }
  return (
    <>
      <Encabezado
        chip="Cuenta creada"
        titulo={nombre ? `Listo, ${nombre},` : "Listo,"}
        resto="ya tienes cuenta"
        sub={
          <>
            {/* Ronda 2 (revisor): decía "Tienes 14 días" siempre, incluso a
                quien ya venció su prueba corta (en esa ronda, 7 días fijos;
                desde v3 "Sin reloj al registrarse" la demo no tiene número
                fijo, arranca con la 1a generación, #1133). Se deriva de
                trial_ends_at real; sin fecha vigente, no se promete un
                número. */}
            {diasPrueba ? (
              <>
                Tienes <strong>{diasPrueba} día{diasPrueba === 1 ? "" : "s"}</strong> para usar
                SmartDx completo.{" "}
              </>
            ) : (
              ""
            )}
          </>
        }
      />
    </>
  );
};

// ── Quién eres (#1599, mockup B) ─────────────────────────────────────────────
// Cubre R13, R14, R16, R17, R31, R35, R44, R45, R46, R47 y R54 —
// .claude/specs/1599-onboarding-calificar/. Las piezas de pregunta son UNA sola
// (`Pregunta`) y las pintan tanto las 4 pantallas del wizard como la pantalla de
// lo que falta (`CompletarDatos`): dos pantallas que preguntan lo mismo no pueden
// verse distinto.

/** Circulito de "ya respondida" a la izquierda de cada pregunta (`.st` del mockup). */
const Estado = ({ ok }) => (
  <Box
    component="span"
    aria-hidden="true"
    sx={{
      width: 17,
      height: 17,
      borderRadius: "50%",
      border: "1.5px solid",
      borderColor: ok ? "var(--color-success)" : "rgba(var(--fg-rgb),0.3)",
      backgroundColor: ok ? "var(--color-success)" : "transparent",
      color: "var(--text-on-success)",
      flex: "none",
      display: "grid",
      placeItems: "center",
    }}
  >
    {ok && <Ic.Ok size={11} strokeWidth="3" />}
  </Box>
);

/**
 * Título de una pregunta (`lab` del mockup). La marca "opcional" va SOLO en las que lo son (R54):
 * herramientas y frustraciones (`esOpcional`) y la universidad de médico general y especialista
 * (#1738 R14, que llega por `opcional` porque depende de la etapa). El canal ya NO la lleva (R18).
 */
const TituloPregunta = ({ texto, ok, nota, campo, id, opcional }) => (
  <Box
    component={id ? "label" : "div"}
    {...(id ? { htmlFor: id } : {})}
    sx={{
      display: "flex",
      gap: "8px",
      alignItems: "center",
      fontSize: 13.5,
      color: "var(--text-muted)",
      mb: "8px",
    }}
  >
    <Estado ok={ok} />
    <span>{texto}</span>
    {(opcional === undefined ? esOpcional(campo) : opcional) && (
      <Box
        component="span"
        data-testid="onboarding-opcional"
        data-campo={campo}
        sx={{
          font: '600 11px "SF Mono", ui-monospace, Menlo, Consolas, monospace',
          letterSpacing: ".04em",
          textTransform: "uppercase",
          color: "var(--text-faint)",
          border: "1px solid rgba(var(--fg-rgb),0.22)",
          borderRadius: 999,
          p: "1px 8px",
          flex: "none",
        }}
      >
        opcional
      </Box>
    )}
    {nota && (
      <Box
        component="small"
        sx={{
          color: "var(--text-faint)",
          fontSize: 12.5,
          ml: "auto",
          // #1850: en el celular la nota se partía en 2 líneas (sobre todo junto a «opcional») y
          // empujaba el Continuar fuera de la vista; en el computador se queda.
          display: { xs: "none", md: "inline" },
        }}
      >
        {nota}
      </Box>
    )}
  </Box>
);

const Aviso = ({ children, tono = "faint", ...rest }) => (
  <Box
    {...rest}
    sx={{ fontSize: 13, mt: "6px", color: tono === "danger" ? "var(--color-danger)" : "var(--text-faint)" }}
  >
    {children}
  </Box>
);

/** Bloque de una pregunta hija (la que abre otra respuesta): raya del acento a la izquierda. */
export const Hijo = ({ children }) => (
  <Box
    sx={{
      borderLeft: "2px solid var(--accent-onboarding)",
      pl: "14px",
      display: "grid",
      gap: "12px",
    }}
  >
    {children}
  </Box>
);

// #1738 R3 y R14: input con lista desplegable PROPIA para «De qué especialidad» y «Dónde estudias /
// estudiaste / Universidad de tu beca». Sin MUI Autocomplete: los overrides globales de theme.js
// (alto de 40 px, colores de menú) lo deforman y no se ven leyendo el JSX (mismo motivo que
// `Opcion`). Filtra sin tildes ni mayúsculas y acepta texto libre: lo escrito es la respuesta,
// elegir de la lista solo lo completa. UN solo componente para los dos campos (la v2 pidió no
// copiarlo). Cubre R3, R14 — .claude/specs/1738-etapa-alta/
//
// TRAMPA (revisor v3, #1738): la lista NO se cierra en el `blur` del input. Con la lista en el
// flujo del layout, el mousedown sobre «Guardar y entrar» o «Continuar» disparaba el blur, la lista
// desaparecía, el botón subía 50 px antes del mouseup y el click caía en el vacío: el primer toque
// no hacía nada (9 de 9 en mouse y touch). La solución obvia (cerrar en blur con un setTimeout) es
// una carrera; acá se cierra con el `click` que llega DESPUÉS de que el botón ya recibió el suyo
// (listener de documento en burbuja), con Tab y con Escape. Cubre R3, R14 y R23.
const CampoConLista = ({ campo, id, testid, titulo, opciones: lista, valor, onChange, ok, opcional, maxLength, placeholder }) => {
  const [abierto, setAbierto] = React.useState(false);
  const [activa, setActiva] = React.useState(0);
  const cajaRef = React.useRef(null);
  // Un click fuera de la caja cierra la lista, pero recién cuando ese click ya llegó a su destino.
  React.useEffect(() => {
    if (!abierto) return undefined;
    const alClickear = (ev) => {
      if (cajaRef.current && !cajaRef.current.contains(ev.target)) setAbierto(false);
    };
    document.addEventListener("click", alClickear);
    return () => document.removeEventListener("click", alClickear);
  }, [abierto]);
  const opciones = filtrarOpciones(lista, valor);
  // Si ya escribió exactamente una de la lista no hay nada que sugerir.
  const exacta = opciones.length === 1 && sinTildes(opciones[0]) === sinTildes(valor);
  const visible = abierto && opciones.length > 0 && !exacta;
  const elegir = (e) => {
    onChange(e);
    setAbierto(false);
  };
  const q = sinTildes(valor);
  const resaltar = (e) => {
    const i = sinTildes(e).indexOf(q);
    if (i < 0 || !q) return e;
    return (
      <>
        {e.slice(0, i)}
        <Box component="b" sx={{ color: "var(--text-primary)", fontWeight: 600 }}>
          {e.slice(i, i + q.length)}
        </Box>
        {e.slice(i + q.length)}
      </>
    );
  };
  return (
    <Box ref={cajaRef}>
      <TituloPregunta texto={titulo} ok={ok} id={id} opcional={opcional} campo={campo} />
      <Box
        component="input"
        id={id}
        data-testid={testid}
        role="combobox"
        aria-expanded={visible}
        aria-controls={`${id}-lista`}
        aria-autocomplete="list"
        sx={campoSx}
        value={valor}
        maxLength={maxLength}
        placeholder={placeholder}
        autoComplete="off"
        aria-label={titulo}
        onChange={(e) => {
          onChange(e.target.value);
          setAbierto(true);
          setActiva(0);
        }}
        onFocus={() => setAbierto(true)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" && visible) {
            e.preventDefault();
            setActiva((i) => Math.min(i + 1, opciones.length - 1));
          } else if (e.key === "ArrowUp" && visible) {
            e.preventDefault();
            setActiva((i) => Math.max(i - 1, 0));
          } else if (e.key === "Enter" && visible) {
            e.preventDefault();
            elegir(opciones[activa] || opciones[0]);
          } else if (e.key === "Escape" || e.key === "Tab") {
            setAbierto(false);
          }
        }}
      />
      {visible && (
        <Box
          id={`${id}-lista`}
          role="listbox"
          data-testid={`${testid}-lista`}
          sx={{
            backgroundColor: "var(--bg-elevated)",
            border: "1px solid rgba(var(--fg-rgb),0.16)",
            borderRadius: "12px",
            mt: "6px",
            overflow: "hidden",
          }}
        >
          {opciones.map((e, i) => (
            <Box
              key={e}
              role="option"
              aria-selected={i === activa}
              data-testid={`${testid}-opcion`}
              // preventDefault en mouseDown: elegir una sugerencia no le quita el foco al input (en el celular,
              // no baja el teclado). La lista ya no se cierra en el blur; la cierra `elegir` o el click de afuera.
              onMouseDown={(ev) => {
                ev.preventDefault();
                elegir(e);
              }}
              onMouseEnter={() => setActiva(i)}
              sx={{
                p: "10px 14px",
                fontSize: 14.5,
                color: "var(--text-muted)",
                cursor: "pointer",
                minHeight: 44,
                display: "flex",
                alignItems: "center",
                borderTop: i ? "1px solid rgba(var(--fg-rgb),0.08)" : "none",
                backgroundColor: i === activa ? "rgba(var(--color-primary-rgb),0.14)" : "transparent",
              }}
            >
              <span>{resaltar(e)}</span>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const campoMalSx = { borderColor: "rgba(var(--color-danger-rgb),0.6)" };

// `data-testid` del texto de otro (R46): los del spec, no un derivado del nombre del campo.
const TESTID_OTRO = {
  herramientas_turno_otra: "onboarding-herramientas-otra",
  frustraciones_otro: "onboarding-frustraciones-otro",
  como_nos_conocio_otro: "onboarding-como-nos-conocio-otro",
};

const OtroTexto = ({ campo, valor, onChange, placeholder, etiqueta }) => (
  <Hijo>
    <Box>
      <Box
        component="input"
        data-testid={TESTID_OTRO[campo]}
        sx={campoSx}
        value={valor}
        maxLength={120}
        placeholder={placeholder}
        autoComplete="off"
        aria-label={etiqueta}
        onChange={(e) => onChange(e.target.value)}
      />
    </Box>
  </Hijo>
);

const fraseHoy = (hoy) => {
  // "Hoy es sábado 10 de octubre." Se arma desde el YYYY-MM-DD de Santiago al mediodía UTC para
  // que el huso del navegador no corra el día.
  try {
    const [y, m, d] = hoy.split("-").map(Number);
    const texto = new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString("es-CL", {
      weekday: "long",
      day: "numeric",
      month: "long",
      timeZone: "UTC",
    });
    return `Hoy es ${texto.replace(",", "")}.`;
  } catch (_) {
    return "";
  }
};

/**
 * UNA pregunta de Quién eres. `v` son las respuestas, `onCambio(campo, valor)` las cambia y
 * `hoy` (YYYY-MM-DD de Santiago) es el mínimo del control de fecha. Los `data-testid` y los
 * `data-valor` son los del spec (R13, R16, R17, R35, R44-R46).
 */
export const Pregunta = ({ campo, v, onCambio, hoy, diasEnCuatro = false }) => {
  const ok = campoRespondido(campo, v, hoy);
  const poner = (valor) => onCambio(campo, valor);

  if (campo === "full_name") {
    const mal = Boolean(v.full_name.trim()) && !ok;
    return (
      <Box>
        <TituloPregunta texto="Tu nombre completo" ok={ok} nota="nombre y apellido" id="onb-nombre-completo" />
        <Box
          component="input"
          id="onb-nombre-completo"
          data-testid="onboarding-nombre"
          sx={{ ...campoSx, ...(mal ? campoMalSx : {}) }}
          value={v.full_name}
          maxLength={120}
          placeholder="Ej: Paula Soto"
          autoComplete="off"
          aria-label="Tu nombre completo"
          onChange={(e) => poner(e.target.value)}
        />
        {/* #1850: la ayuda «Es con lo que firmas tus notas» salió para que la pantalla quepa en
            una vista; el error de nombre incompleto se sigue mostrando. */}
        {mal && <Aviso tono="danger">Escribe tu nombre y apellido.</Aviso>}
      </Box>
    );
  }

  if (campo === "etapa") {
    return (
      <Box>
        <TituloPregunta texto="En qué etapa estás" ok={ok} />
        <Box role="radiogroup" aria-label="En qué etapa estás" sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {ETAPAS.map((o) => (
            <Opcion key={o.value} data-valor={o.value} activo={v.etapa === o.value} onClick={() => poner(o.value)}>
              {o.label}
            </Opcion>
          ))}
        </Box>
      </Box>
    );
  }

  if (campo === "especialidad") {
    return (
      <CampoConLista
        campo={campo}
        id="onb-especialidad"
        testid="onboarding-especialidad"
        titulo="De qué especialidad"
        opciones={ESPECIALIDADES}
        valor={v.especialidad}
        onChange={poner}
        ok={ok}
        maxLength={120}
        placeholder="Ej: Cardiología"
      />
    );
  }

  // #1738 R14: «Dónde estudias» (estudiante, interno), «Universidad de tu beca» (becado) o
  // «Dónde estudiaste» con marca opcional (médico general, especialista).
  if (campo === "universidad") {
    return (
      <CampoConLista
        campo={campo}
        id="onb-universidad-alta"
        testid="onboarding-universidad"
        titulo={tituloUniversidad(v.etapa)}
        opciones={UNIVERSIDADES}
        valor={v.universidad}
        onChange={poner}
        ok={ok}
        opcional={esOpcionalEn(campo, v)}
        maxLength={200}
        placeholder="Ej: Universidad de Concepción"
      />
    );
  }

  // #1738 R4: "Qué eres" cuando la etapa es No soy médico (los `value` son los de `Profession`).
  if (campo === "profesion_no_medico") {
    return (
      <Box>
        <TituloPregunta texto="Qué eres" ok={ok} />
        <Box
          role="radiogroup"
          aria-label="Qué eres"
          data-testid="onboarding-profesion-no-medico"
          sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}
        >
          {PROFESIONES_NO_MEDICO.map((o) => (
            <Opcion
              key={o.value}
              data-valor={o.value}
              activo={v.profesion_no_medico === o.value}
              onClick={() => poner(o.value)}
            >
              {o.label}
            </Opcion>
          ))}
        </Box>
      </Box>
    );
  }

  if (campo === "donde_trabaja") {
    return (
      <Box>
        <TituloPregunta texto={textosDeEtapa(v.etapa).donde} ok={ok} id="onb-donde" />
        <Box
          component="input"
          id="onb-donde"
          sx={campoSx}
          value={v.donde_trabaja}
          maxLength={160}
          placeholder={textosDeEtapa(v.etapa).dondePh}
          autoComplete="off"
          aria-label={textosDeEtapa(v.etapa).donde}
          onChange={(e) => poner(e.target.value)}
        />
      </Box>
    );
  }

  if (campo === "ambito") {
    return (
      <Box>
        <TituloPregunta texto="Dónde ejerces" ok={ok} nota="marca las que apliquen" />
        <Box role="group" aria-label="Dónde ejerces" sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {AMBITOS.map((o) => (
            <Opcion
              key={o.value}
              role="checkbox"
              data-valor={o.value}
              activo={v.ambito.includes(o.value)}
              onClick={() => poner(alternarEnLista(v.ambito, o.value))}
            >
              {v.ambito.includes(o.value) && <Ic.Ok size={16} />}
              {o.label}
            </Opcion>
          ))}
        </Box>
      </Box>
    );
  }

  if (campo === "carga_documentacion") {
    return (
      <Box>
        <TituloPregunta texto={textosDeEtapa(v.etapa).carga} ok={ok} />
        <Box role="radiogroup" aria-label={textosDeEtapa(v.etapa).carga} sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {CARGAS.map((o) => (
            <Opcion
              key={o.value}
              data-valor={o.value}
              activo={v.carga_documentacion === o.value}
              onClick={() => poner(o.value)}
            >
              {o.label}
            </Opcion>
          ))}
        </Box>
      </Box>
    );
  }

  if (campo === "turnos_semana") {
    return (
      <Box data-testid="onboarding-turnos">
        <TituloPregunta texto={textosDeEtapa(v.etapa).turnos} ok={ok} />
        <Box
          role="radiogroup"
          aria-label={textosDeEtapa(v.etapa).turnos}
          sx={{ display: "grid", gap: "8px", gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}
        >
          {TURNOS.map((o) => (
            <Opcion key={o.value} grande data-valor={o.value} activo={v.turnos_semana === o.value} onClick={() => poner(o.value)}>
              {o.label}
            </Opcion>
          ))}
        </Box>
      </Box>
    );
  }

  if (campo === "trabaja_hospitalizados") {
    return (
      <Box data-testid="onboarding-hospitalizados">
        <TituloPregunta texto="El resto del tiempo trabajas con pacientes hospitalizados?" ok={ok} />
        <Box
          role="radiogroup"
          aria-label="El resto del tiempo trabajas con pacientes hospitalizados?"
          sx={{ display: "grid", gap: "8px", gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}
        >
          {HOSPITALIZADOS.map((o) => (
            <Opcion
              key={o.label}
              grande
              data-valor={String(o.value)}
              activo={v.trabaja_hospitalizados === o.value}
              onClick={() => poner(o.value)}
            >
              {o.label}
            </Opcion>
          ))}
        </Box>
      </Box>
    );
  }

  if (campo === "dias_hospitalizados") {
    return (
      <Box data-testid="onboarding-dias-hosp">
        <TituloPregunta texto="Qué días trabajas con hospitalizados?" ok={ok} nota="marca los que apliquen" />
        <Box
          role="group"
          aria-label="Qué días trabajas con hospitalizados?"
          sx={{ display: "grid", gap: "4px", gridTemplateColumns: diasEnCuatro ? "repeat(4, minmax(0, 1fr))" : "repeat(7, minmax(0, 1fr))" }}
        >
          {DIAS_SEMANA.map((o) => (
            <Opcion
              key={o.value}
              role="checkbox"
              forma="dia"
              data-valor={o.value}
              activo={v.dias_hospitalizados.includes(o.value)}
              onClick={() => poner(alternarEnLista(v.dias_hospitalizados, o.value))}
            >
              {o.label}
            </Opcion>
          ))}
        </Box>
      </Box>
    );
  }

  if (campo === "tipos_turno") {
    return (
      <Box data-testid="onboarding-tipos-turno">
        <TituloPregunta texto="Qué tipo de turnos haces?" ok={ok} nota="marca los que apliquen" />
        {TIPOS_TURNO_GRUPOS.map((g, i) => (
          <React.Fragment key={g.titulo}>
            <Box
              sx={{
                fontSize: 12,
                letterSpacing: ".08em",
                textTransform: "uppercase",
                color: "var(--text-faint)",
                mt: i ? "10px" : "2px",
                mb: "6px",
                fontWeight: 600,
              }}
            >
              {g.titulo}
            </Box>
            <Box role="group" aria-label={g.titulo} sx={{ display: "grid", gap: "8px", gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
              {g.opciones.map((o) => (
                <Opcion
                  key={o.value}
                  role="checkbox"
                  forma="tarjeta"
                  sub={o.sub}
                  data-valor={o.value}
                  activo={v.tipos_turno.includes(o.value)}
                  onClick={() => poner(alternarEnLista(v.tipos_turno, o.value))}
                >
                  {o.label}
                </Opcion>
              ))}
            </Box>
          </React.Fragment>
        ))}
      </Box>
    );
  }

  if (campo === "proximo_turno") {
    const mal = Boolean(v.proximo_turno) && !ok;
    return (
      <Box>
        <TituloPregunta texto="Cuándo es tu próximo turno?" ok={ok} id="onb-proximo-turno" />
        <Box
          component="input"
          id="onb-proximo-turno"
          type="date"
          data-testid="onboarding-proximo-turno"
          min={hoy}
          max={maxProximoTurno(hoy)}
          sx={{ ...campoSx, ...(mal ? campoMalSx : {}) }}
          value={v.proximo_turno}
          aria-label="Fecha de tu próximo turno"
          onChange={(e) => poner(e.target.value)}
        />
        {mal ? (
          <Aviso tono="danger">Elige una fecha entre hoy y el próximo año.</Aviso>
        ) : (
          <Aviso>{`${fraseHoy(hoy)} Elige hoy o una fecha futura.`.trim()}</Aviso>
        )}
      </Box>
    );
  }

  if (campo === "que_hace_sin_turnos") {
    const largo = v.que_hace_sin_turnos.length;
    return (
      <Box>
        <TituloPregunta texto="Qué haces" ok={ok} nota="obligatorio, máximo 200 caracteres" id="onb-que-hace" />
        <Box
          component="textarea"
          id="onb-que-hace"
          data-testid="onboarding-que-hace"
          rows={3}
          maxLength={200}
          sx={{ ...campoSx, resize: "vertical", minHeight: 96, lineHeight: 1.45 }}
          value={v.que_hace_sin_turnos}
          placeholder="Ej: policlínico de medicina interna, consulta privada, investigación"
          aria-label="Qué haces"
          onChange={(e) => poner(e.target.value)}
        />
        <Box sx={{ fontSize: 12.5, color: "var(--text-faint)", textAlign: "right", mt: "4px" }}>{largo}/200</Box>
      </Box>
    );
  }

  if (campo === "frustraciones") {
    return (
      <Box data-testid="onboarding-frustraciones">
        <TituloPregunta texto="Qué te frustra hoy" ok={ok} nota="marca las que apliquen" campo={campo} />
        <Box role="group" aria-label="Qué te frustra hoy" sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {FRUSTRACIONES_OPCIONES.map((o) => (
            <Opcion
              key={o.value}
              role="checkbox"
              data-valor={o.value}
              activo={v.frustraciones.includes(o.value)}
              onClick={() => poner(alternarEnLista(v.frustraciones, o.value))}
            >
              {v.frustraciones.includes(o.value) && <Ic.Ok size={16} />}
              {o.label}
            </Opcion>
          ))}
        </Box>
      </Box>
    );
  }

  if (campo === "equipo_turno") {
    return (
      <Box data-testid="onboarding-equipo">
        <TituloPregunta texto={textosDeEtapa(v.etapa).equipo} ok={ok} nota="marca los que apliquen" campo={campo} />
        {/* #1850 (revisor v1, R3): en grilla de 4 en una fila; con flex «Nadie» bajaba a una 2ª línea
            y empujaba el Continuar fuera de la vista en 390×844. */}
        <Box
          role="group"
          aria-label={textosDeEtapa(v.etapa).equipo}
          sx={{ display: "grid", gap: "6px", gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}
        >
          {EQUIPO_OPCIONES.map((o) => (
            <Opcion
              key={o.value}
              grande
              role="checkbox"
              data-valor={o.value}
              activo={v.equipo_turno.includes(o.value)}
              onClick={() => poner(alternarEquipo(v.equipo_turno, o.value))}
            >
              {o.label}
            </Opcion>
          ))}
        </Box>
      </Box>
    );
  }

  if (campo === "herramientas_turno") {
    return (
      <Box data-testid="onboarding-herramientas">
        <TituloPregunta texto="Herramientas que usas en tus turnos" ok={ok} nota="marca las que apliquen" campo={campo} />
        <Box role="group" aria-label="Herramientas que usas en tus turnos" sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {HERRAMIENTAS_OPCIONES.map((o) => (
            <Opcion
              key={o.value}
              role="checkbox"
              data-valor={o.value}
              activo={v.herramientas_turno.includes(o.value)}
              onClick={() => poner(alternarHerramienta(v.herramientas_turno, o.value))}
            >
              {v.herramientas_turno.includes(o.value) && <Ic.Ok size={16} />}
              {o.label}
            </Opcion>
          ))}
        </Box>
      </Box>
    );
  }

  if (campo === "como_nos_conocio") {
    return (
      <Box data-testid="onboarding-como-nos-conocio">
        <TituloPregunta texto="Cómo nos conociste" ok={ok} nota="elige una" campo={campo} />
        <Box role="radiogroup" aria-label="Cómo nos conociste" sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {CANALES_OPCIONES.map((o) => (
            <Opcion key={o.value} data-valor={o.value} activo={v.como_nos_conocio === o.value} onClick={() => poner(o.value)}>
              {o.label}
            </Opcion>
          ))}
        </Box>
      </Box>
    );
  }

  if (campo === "herramientas_turno_otra") {
    return (
      <OtroTexto
        campo={campo}
        valor={v.herramientas_turno_otra}
        onChange={poner}
        placeholder="Cuál otra (opcional)"
        etiqueta="Herramienta, otra"
      />
    );
  }
  if (campo === "frustraciones_otro") {
    return (
      <OtroTexto
        campo={campo}
        valor={v.frustraciones_otro}
        onChange={poner}
        placeholder="Cuéntanos qué más te frustra (opcional)"
        etiqueta="Qué te frustra, otro"
      />
    );
  }
  if (campo === "como_nos_conocio_otro") {
    return (
      <OtroTexto
        campo={campo}
        valor={v.como_nos_conocio_otro}
        onChange={poner}
        placeholder="Ej: un correo, un curso (opcional)"
        etiqueta="Cómo nos conociste, otro"
      />
    );
  }
  return null;
};

/** Pregunta con sus condicionales anidadas (`hijo` del mockup): lo que su respuesta abre va debajo, con la raya. */
export const PreguntaConHijos = ({ campo, hijos = [], ...props }) => (
  <Box sx={{ display: "grid", gap: "18px" }}>
    <Pregunta campo={campo} {...props} />
    {hijos.length > 0 && (
      <Hijo>
        {hijos.map((h) => (
          <Pregunta key={h} campo={h} {...props} />
        ))}
      </Hijo>
    )}
  </Box>
);

// Textos de cabecera de cada pantalla (copy del mockup B).
const CABECERA_PANTALLA = {
  [VISTAS.TU]: {
    etq: "Quién eres · 1 de 6",
    titulo: "Cuéntanos",
    resto: "quién eres",
    sub: "Nos sirven para acompañarte mejor. Esta vez son todas obligatorias.",
    pequeno: true,
    extra: "todas obligatorias",
  },
  // #1850: «Tu trabajo» y «Dónde vive tu turno» salen de las pantallas 1 y 2 de antes.
  [VISTAS.TU_TRABAJO]: {
    etq: "Quién eres · 2 de 6",
    titulo: "Tu",
    resto: "trabajo",
    conNombre: "cuéntanos de tu trabajo",
    sub: "Dónde trabajas y cuánto se te va escribiendo en cada turno.",
    extra: "todas obligatorias",
  },
  [VISTAS.SEMANA]: {
    etq: "Quién eres · 3 de 6",
    titulo: "Y cómo es",
    resto: "tu semana",
    conNombre: "cómo es tu semana",
    sub: "Cuántos turnos haces y si trabajas con hospitalizados.",
    extra: "todas obligatorias",
  },
  [VISTAS.LUGARES]: {
    etq: "Quién eres · 4 de 6",
    titulo: "Dónde vive",
    resto: "tu turno hoy",
    sub: "Qué te frustra y qué herramientas usas. Las dos son opcionales.",
    extra: "",
  },
  [VISTAS.TU_TURNO]: {
    etq: "Quién eres · 5 de 6",
    titulo: "Tu",
    resto: "turno",
    sub: "Esto nos dice qué tipo de turnos haces y cuándo te toca el próximo.",
    extra: "todas obligatorias",
  },
  [VISTAS.QUE_HACES]: {
    etq: "Quién eres · 5 de 6",
    titulo: "Cuéntanos",
    resto: "qué haces",
    sub: "Como no haces turnos, dinos en qué trabajas para ajustar SmartDx a ti.",
    extra: "todas obligatorias",
  },
  [VISTAS.COMO]: {
    etq: "Quién eres · 6 de 6",
    titulo: "Una última",
    resto: "pregunta",
    sub: "Nos dice dónde encontrar a más colegas como tú.",
    extra: "todas obligatorias",
  },
};

/** "N de M respondidas, todas obligatorias" (`req` del mockup), o "Opcional" si la pantalla no tiene obligatorias. */
const Requeridas = ({ hechas, total, extra }) => (
  <Box
    data-testid={total ? undefined : "onboarding-todo-opcional"}
    sx={{
      // #1850: en el celular no se muestra (el Continuar ya dice cuántas faltan) y su lugar lo
      // toma la cuenta de horas, para que la pantalla quepa en una vista.
      display: "none",
      alignItems: "center",
      gap: "8px",
      mt: "14px",
      fontSize: 13,
      color: "var(--text-muted)",
      backgroundColor: "rgba(var(--color-primary-rgb),0.12)",
      border: "1px solid rgba(var(--color-primary-rgb),0.3)",
      borderRadius: 999,
      p: "5px 13px 5px 6px",
    }}
  >
    <Box
      component="span"
      sx={{
        backgroundColor: "var(--text-primary)",
        color: "var(--bg-base)",
        borderRadius: 999,
        p: "1px 9px",
        fontWeight: 600,
        fontSize: 12.5,
      }}
    >
      {total ? `${hechas} de ${total}` : "Opcional"}
    </Box>
    {total ? `respondidas, ${extra}` : "puedes seguir sin responder"}
  </Box>
);

const ETIQUETA_HERRAMIENTA = Object.fromEntries(HERRAMIENTAS_OPCIONES.map((o) => [o.value, o.label]));

const girarAtras = keyframes`from { transform: rotate(0deg); } to { transform: rotate(-360deg); }`;
const entrar = keyframes`from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; }`;
const aparecer = keyframes`0% { opacity: 0; transform: scale(.6) translateY(6px); } 70% { transform: scale(1.06); } 100% { opacity: 1; transform: none; }`;
const latir = keyframes`0% { box-shadow: 0 0 0 0 rgba(var(--color-primary-rgb), .45); } 100% { box-shadow: 0 0 0 12px rgba(var(--color-primary-rgb), 0); }`;

/** Número que sube desde el valor anterior hasta `objetivo` (o salta directo con movimiento reducido). */
const useContador = (objetivo, animar) => {
  const [n, setN] = React.useState(objetivo || 0);
  const ultimo = React.useRef(0);
  React.useEffect(() => {
    if (objetivo == null) return undefined;
    const desde = ultimo.current;
    ultimo.current = objetivo;
    if (!animar || desde === objetivo) {
      setN(objetivo);
      return undefined;
    }
    let raf;
    const t0 = performance.now();
    const paso = (t) => {
      const k = Math.min(1, (t - t0) / 900);
      setN(desde + (objetivo - desde) * (1 - (1 - k) ** 3));
      if (k < 1) raf = requestAnimationFrame(paso);
    };
    raf = requestAnimationFrame(paso);
    return () => cancelAnimationFrame(raf);
  }, [objetivo, animar]);
  return n;
};

/** El reloj del lema: el anillo se llena con cada pantalla y la aguja gira hacia atrás al llegar. */
const RelojAtras = ({ avance, animar }) => {
  const r = 11;
  const largo = 2 * Math.PI * r;
  return (
    <Box component="svg" viewBox="0 0 28 28" sx={{ width: 26, height: 26, flex: "none" }} aria-hidden>
      <circle cx="14" cy="14" r={r} fill="none" stroke="rgba(var(--fg-rgb),0.16)" strokeWidth="2.5" />
      <circle
        cx="14"
        cy="14"
        r={r}
        fill="none"
        stroke="var(--accent-onboarding)"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeDasharray={largo}
        strokeDashoffset={largo * (1 - avance)}
        transform="rotate(-90 14 14)"
        style={{ transition: "stroke-dashoffset 700ms cubic-bezier(0.22,1,0.36,1)" }}
      />
      <Box
        component="g"
        sx={{ transformOrigin: "14px 14px", animation: animar ? `${girarAtras} 1100ms cubic-bezier(0.22,1,0.36,1)` : "none" }}
      >
        <line x1="14" y1="14" x2="14" y2="8" stroke="var(--text-primary)" strokeWidth="2" strokeLinecap="round" />
        <line x1="14" y1="14" x2="18" y2="14" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" />
      </Box>
    </Box>
  );
};

const Grande = ({ children, brillo, ...rest }) => (
  <Box
    key={brillo}
    {...rest}
    sx={{
      animation: brillo != null ? `${brilloCifra} 900ms ease-out` : "none",
      ...sinMovimiento,
      fontSize: { xs: 22, md: 24 },
      fontWeight: 600,
      letterSpacing: "-0.02em",
      fontVariantNumeric: "tabular-nums",
      color: "var(--text-primary)",
      lineHeight: 1.15,
    }}
  >
    {children}
  </Box>
);
const Chica = ({ children }) => <Box sx={{ fontSize: 12.5, color: "var(--text-muted)", mt: "3px" }}>{children}</Box>;

/**
 * La cuenta de horas en vivo arriba de cada pantalla de Quién eres (#1850). El video de la
 * bienvenida muestra la cuenta genérica (10.000 h en 20 años); acá se vuelve la del médico con sus
 * respuestas, y cada pantalla muestra una cara distinta para que no se repita el mismo número:
 * saludo → horas por turno sobre un turno de 24 h → horas al año que suben, con equivalencias que
 * rotan → sus herramientas juntándose en una → cuántos días faltan para su turno → el cierre.
 * Fórmula en `helpers/onboarding-horas.js`. Con `prefers-reduced-motion` no anima nada.
 */
export const CuentaHoras = ({ v, vista, hoy }) => {
  const animar = !useMediaQuery("(prefers-reduced-motion: reduce)");
  const recorrido = pantallasQuienEres(v);
  const indice = Math.max(0, recorrido.indexOf(vista));
  const anual = horasAlAno(v);
  const porTurno = horasPorTurno(v);
  const lugares = lugaresDelTurno(v);
  const nAnual = useContador(anual, animar);
  const nTurno = useContador(porTurno, animar);
  const fmt = (n) => Math.round(n).toLocaleString("es-CL");
  const reaccionT = reaccionTurnos(v.turnos_semana, anual);
  const eq = [reaccionEquipo(v.equipo_turno), reaccionT, ...equivalencias(anual)].filter(Boolean);
  const [iEq, setIEq] = React.useState(0);
  React.useEffect(() => {
    if (!animar || eq.length < 2) return undefined;
    const t = setInterval(() => setIEq((i) => (i + 1) % eq.length), 2600);
    return () => clearInterval(t);
  }, [animar, eq.length]);
  const primerNombre = (v.full_name || "").trim().split(/\s+/)[0];
  const dias = diasHasta(v.proximo_turno, hoy);

  let cuerpo;
  if (vista === VISTAS.TU) {
    cuerpo = (
      <>
        <Grande>{primerNombre && primerNombre.length > 1 ? `Hola, ${primerNombre}.` : "Hola."}</Grande>
        <Box key={v.etapa || "sin-etapa"} sx={{ animation: animar ? `${entrar} 400ms both` : "none" }}>
          <Chica>{textosDeEtapa(v.etapa).reaccion || "En 6 pasos sacamos cuánto de tu turno se va escribiendo."}</Chica>
        </Box>
      </>
    );
  } else if (vista === VISTAS.TU_TRABAJO) {
    const llenas = porTurno == null ? 0 : Math.max(1, Math.round(porTurno));
    cuerpo = (
      <>
        <Grande brillo={porTurno}>{porTurno == null ? "Cuánto se te va escribiendo?" : `≈ ${nTurno.toLocaleString("es-CL", { maximumFractionDigits: 1 })} h de cada turno`}</Grande>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(24, 1fr)", gap: "2px", mt: "8px" }} aria-hidden>
          {Array.from({ length: 24 }, (_, i) => (
            <Box
              key={`${i}-${llenas}`}
              sx={{
                height: 9,
                borderRadius: "3px",
                backgroundColor: i < llenas ? "var(--accent-onboarding)" : "rgba(var(--fg-rgb),0.1)",
                animation: i < llenas && animar ? `${aparecer} 420ms ${i * 60}ms both` : "none",
              }}
            />
          ))}
        </Box>
        <Box key={v.carga_documentacion || "sin-carga"} sx={{ animation: animar ? `${entrar} 400ms both` : "none" }}>
          <Chica>{porTurno == null ? "Elige abajo y lo marcamos sobre un turno de 24 h." : `${reaccionCarga(v.carga_documentacion)} · Turno de 24 h.`}</Chica>
        </Box>
      </>
    );
  } else if (vista === VISTAS.SEMANA) {
    cuerpo =
      anual != null ? (
        <>
          <Grande brillo={anual} data-testid="onboarding-cuenta-horas-numero">≈ {fmt(nAnual)} h al año escribiendo</Grande>
          <Box key={iEq} sx={{ fontSize: 13, color: "var(--text-primary)", mt: "3px", animation: animar ? `${entrar} 400ms both` : "none" }}>
            {eq[iEq % Math.max(1, eq.length)]}
          </Box>
          <Box sx={{ fontSize: 11.5, color: "var(--text-faint)", mt: "2px" }}>{comoSeCalculo(v)}</Box>
        </>
      ) : (
        <>
          <Grande>{porTurno != null ? `≈ ${fmt(porTurno)} h por turno` : "Tus horas al año"}</Grande>
          <Chica>{v.turnos_semana === "0" ? "Sin turnos no hay cuenta por turno: igual te ayudamos con tus hospitalizados." : "Dinos cuántos turnos haces y te damos el total del año."}</Chica>
        </>
      );
  } else if (vista === VISTAS.LUGARES) {
    cuerpo = (
      <>
        <Grande brillo={lugares.length || null}>{lugares.length ? `${lugares.length} ${lugares.length === 1 ? "lugar" : "lugares"} → 1` : "Dónde vive tu turno?"}</Grande>
        <Box sx={{ display: "flex", flexWrap: "nowrap", gap: { xs: "4px", md: "6px" }, mt: "8px", overflow: "hidden", alignItems: "center" }}>
          {lugares.map((h, i) => (
            <Box
              key={h}
              component="span"
              sx={{
                flex: "none",
                fontSize: { xs: 11, md: 12 },
                p: { xs: "2px 7px", md: "3px 9px" },
                borderRadius: "8px",
                border: "1px dashed rgba(var(--fg-rgb),0.25)",
                color: "var(--text-muted)",
                backgroundColor: "var(--bg-input)",
                animation: animar ? `${aparecer} 420ms ${i * 120}ms both` : "none",
              }}
            >
              {ETIQUETA_HERRAMIENTA[h] || h}
            </Box>
          ))}
          {lugares.length > 0 && (
            <Box
              component="span"
              sx={{
                flex: "none",
                fontSize: { xs: 11, md: 12 },
                fontWeight: 600,
                p: { xs: "2px 8px", md: "3px 10px" },
                borderRadius: "8px",
                color: "var(--text-primary)",
                backgroundColor: "rgba(var(--color-primary-rgb),0.35)",
                animation: animar ? `${aparecer} 480ms ${lugares.length * 120 + 150}ms both, ${latir} 1.4s ${lugares.length * 120 + 600}ms 2` : "none",
              }}
            >
              → SmartDx
            </Box>
          )}
        </Box>
        <Chica>{lugares.length ? (anual ? `Tus ${fmt(anual)} h al año, en un solo lugar.` : "Todo tu turno, en un solo lugar.") : "Marca abajo lo que usas hoy."}</Chica>
      </>
    );
  } else if (vista === VISTAS.TU_TURNO || vista === VISTAS.QUE_HACES) {
    const cuando = dias == null ? null : dias <= 0 ? "Hoy" : dias === 1 ? "Mañana" : `En ${dias} días`;
    cuerpo = (
      <>
        <Grande brillo={dias}>{cuando ? `${cuando} es tu próximo turno` : vista === VISTAS.QUE_HACES ? "Casi listo" : "Cuándo es tu próximo turno?"}</Grande>
        <Chica>
          {vista === VISTAS.QUE_HACES
            ? "Cuéntanos qué haces y ajustamos SmartDx a eso."
            : "Ese día te recordamos a las 8 para que lo pruebes con tus pacientes."}
        </Chica>
      </>
    );
  } else {
    cuerpo = (
      <>
        <Grande>Último paso</Grande>
        {/* #1850 (revisor v1): sin cifra en «recuperar»: las horas son lo que cuesta escribir, no un
            ahorro medido (R4). */}
        <Chica>y empiezas a recuperar tus horas de turno.</Chica>
      </>
    );
  }

  return (
    <Box
      data-testid="onboarding-cuenta-horas"
      aria-live="polite"
      sx={{
        borderRadius: "14px",
        p: { xs: "10px 12px", md: "10px 14px" },
        mb: "12px",
        border: "1px solid rgba(var(--color-primary-rgb),0.4)",
        background:
          "linear-gradient(135deg, rgba(var(--color-primary-rgb),0.24), rgba(var(--color-primary-rgb),0.04))",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", fontSize: { xs: 11.5, md: 12.5 }, color: "var(--text-muted)", whiteSpace: { xs: "nowrap", md: "normal" } }}>
        <RelojAtras key={indice} avance={(indice + 1) / recorrido.length} animar={animar} />
        <span>
          Cada vez más cerca de{" "}
          <Box component="b" sx={{ color: "var(--text-primary)", fontWeight: 600 }}>
            recuperar tus horas de turno
          </Box>
        </span>
      </Box>
      <Box key={vista} sx={{ mt: "6px", animation: animar ? `${entrar} 450ms cubic-bezier(0.22,1,0.36,1) both` : "none" }}>
        {cuerpo}
      </Box>
    </Box>
  );
};

/** Cartel de error del guardado (R19). */
export const ErrorGuardado = ({ children }) => (
  <Box
    role="alert"
    data-testid="onboarding-error"
    sx={{
      display: "flex",
      gap: "10px",
      alignItems: "flex-start",
      backgroundColor: "rgba(var(--color-danger-rgb),0.12)",
      border: "1px solid rgba(var(--color-danger-rgb),0.4)",
      borderRadius: "14px",
      p: "12px 14px",
      fontSize: 14.5,
      color: "var(--text-primary)",
    }}
  >
    <Box component="span" sx={{ color: "var(--color-danger)", mt: "2px", display: "inline-flex" }}>
      <Svg>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 8v5M12 16.5v.01" />
      </Svg>
    </Box>
    <Box>
      <Box component="b" sx={{ display: "block", fontWeight: 600 }}>
        No pudimos guardar tus respuestas
      </Box>
      {children}
    </Box>
  </Box>
);

/** Continuar con su aviso de cuánto falta, y el cartel de error si el guardado falló. */
export const BloqueCta = ({ faltan, enviando = false, bloqueado = false, onContinuar, error, libre = "", texto = "Continuar" }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: { xs: "stretch", md: "flex-start" } }}>
    {error && <ErrorGuardado>{error}</ErrorGuardado>}
    <Cta data-testid="onboarding-continuar" onClick={onContinuar} disabled={faltan > 0 || bloqueado || enviando}>
      {enviando ? "Guardando..." : texto}
    </Cta>
    {libre && (
      <Box component="span" data-testid="onboarding-puede-seguir" sx={{ fontSize: 13, color: "var(--text-faint)" }}>
        {libre}
      </Box>
    )}
    {faltan > 0 && (
      <Box component="span" sx={{ fontSize: 13, color: "var(--text-faint)" }}>
        {`Te ${faltan === 1 ? "falta 1 respuesta" : `faltan ${faltan} respuestas`} para continuar`}
      </Box>
    )}
  </Box>
);

/**
 * Paso 1 — UNA de las 4 pantallas de Quién eres (#1599). `vista` es la pantalla (`quien_eres`,
 * `semana`, `tu_turno`, `que_haces` o `como_nos_conociste`); los campos que pinta y sus
 * condicionales salen de `camposDePantalla`. Devuelve un fragmento: el cascarón del wizard
 * pone el gap.
 */
export const PasoQuienEres = ({ vista, v, onCambio, hoy }) => {
  const cabBase = CABECERA_PANTALLA[vista];
  // #1850: con el nombre ya escrito, las pantallas siguientes le hablan por su nombre.
  const primer = (v.full_name || "").trim().split(/\s+/)[0];
  const cab =
    primer && primer.length > 1 && vista !== VISTAS.TU && cabBase.conNombre
      ? { ...cabBase, titulo: `${primer},`, resto: cabBase.conNombre }
      : cabBase;
  const { hechas, total } = conteoDePantalla(vista, v, hoy);
  const props = { v, onCambio, hoy };
  const campos = camposDePantalla(vista, v);
  // Las condicionales (días, textos de otro) van anidadas en su pregunta, no sueltas.
  const hijosDe = {
    etapa: ["especialidad", "universidad", "profesion_no_medico"],
    trabaja_hospitalizados: ["dias_hospitalizados"],
    frustraciones: ["frustraciones_otro"],
    herramientas_turno: ["herramientas_turno_otra"],
    como_nos_conocio: ["como_nos_conocio_otro"],
  };
  const sueltos = new Set(Object.values(hijosDe).flat());
  return (
    <>
      <Box>
        <CuentaHoras v={v} vista={vista} hoy={hoy} />
        <Etiqueta>{cab.etq}</Etiqueta>
        <Encabezado pequeno={cab.pequeno} titulo={cab.titulo} resto={cab.resto} sub={cab.sub} compactoMovil />
        <Requeridas hechas={hechas} total={total} extra={cab.extra} />
      </Box>
      <Box
        key={vista}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          animation: `${entrarPantalla} 380ms ${EASE} both`,
          ...sinMovimiento,
        }}
      >
        {campos
          .filter((c) => !sueltos.has(c))
          .map((c) => (
            <PreguntaConHijos key={c} campo={c} hijos={(hijosDe[c] || []).filter((h) => campos.includes(h))} {...props} />
          ))}
      </Box>
    </>
  );
};
